import { createTheme } from '@mui/material/styles';

// フォント設定
const fontFamily = '"Inter", "Noto Sans JP", "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

// 共通のテーマ設定
const baseTheme = {
  typography: {
    fontFamily,
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
      '@media (max-width:600px)': {
        fontSize: '2rem',
      },
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.75rem',
      '@media (max-width:600px)': {
        fontSize: '1.5rem',
      },
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem',
      '@media (max-width:600px)': {
        fontSize: '1.25rem',
      },
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 20,
  },
  spacing: 8,
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '24px',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '20px',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '1rem',
          padding: '12px 32px',
          minHeight: '48px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
          },
        },
        contained: {
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
          '&:hover': {
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.2)',
          },
        },
        large: {
          padding: '16px 40px',
          fontSize: '1.1rem',
          minHeight: '56px',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '16px',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
            },
            '&.Mui-focused': {
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '20px',
          fontWeight: 500,
          fontSize: '0.875rem',
          height: '36px',
          backdropFilter: 'blur(10px)',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'scale(1.05)',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          borderRadius: '12px 12px 0 0',
          fontWeight: 600,
          fontSize: '1rem',
          padding: '16px 24px',
          minHeight: '64px',
          transition: 'all 0.3s ease',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: '24px',
          paddingRight: '24px',
          '@media (max-width:600px)': {
            paddingLeft: '16px',
            paddingRight: '16px',
          },
        },
      },
    },
  },
};

// テーマ1: パステルグリーン（デフォルト）
export const pastelGreenTheme = createTheme({
  ...baseTheme,
  palette: {
    mode: 'light',
    primary: {
      main: '#66BB6A',
      light: '#98EE99',
      dark: '#388E3C',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#81C784',
      light: '#B2DFDB',
      dark: '#4CAF50',
    },
    background: {
      default: '#f8fffe',
      paper: 'rgba(255, 255, 255, 0.9)',
    },
    text: {
      primary: '#1a202c',
      secondary: '#2d3748',
    },
    success: {
      main: '#4CAF50',
    },
    warning: {
      main: '#FF9800',
    },
    error: {
      main: '#F44336',
    },
  },
});

// テーマ2: ソフトブルー
export const softBlueTheme = createTheme({
  ...baseTheme,
  palette: {
    mode: 'light',
    primary: {
      main: '#42A5F5',
      light: '#81D4FA',
      dark: '#1976D2',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#64B5F6',
      light: '#BBDEFB',
      dark: '#1565C0',
    },
    background: {
      default: '#f7fafc',
      paper: 'rgba(255, 255, 255, 0.9)',
    },
    text: {
      primary: '#1a202c',
      secondary: '#2d3748',
    },
    success: {
      main: '#4CAF50',
    },
    warning: {
      main: '#FF9800',
    },
    error: {
      main: '#F44336',
    },
  },
});

// テーマ3: ウォームピンク
export const warmPinkTheme = createTheme({
  ...baseTheme,
  palette: {
    mode: 'light',
    primary: {
      main: '#EC407A',
      light: '#F8BBD9',
      dark: '#C2185B',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#F06292',
      light: '#FCE4EC',
      dark: '#AD1457',
    },
    background: {
      default: '#fdf8f6',
      paper: 'rgba(255, 255, 255, 0.9)',
    },
    text: {
      primary: '#1a202c',
      secondary: '#2d3748',
    },
    success: {
      main: '#4CAF50',
    },
    warning: {
      main: '#FF9800',
    },
    error: {
      main: '#F44336',
    },
  },
});

// テーマ4: ミッドナイト（ダークモード）
export const midnightTheme = createTheme({
  ...baseTheme,
  palette: {
    mode: 'dark',
    primary: {
      main: '#64FFDA',
      light: '#A7FFEB',
      dark: '#00BFA5',
      contrastText: '#000000',
    },
    secondary: {
      main: '#80CBC4',
      light: '#B2DFDB',
      dark: '#26A69A',
    },
    background: {
      default: '#1a1a2e',
      paper: 'rgba(255, 255, 255, 0.1)',
    },
    surface: {
      main: 'rgba(255, 255, 255, 0.05)',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
  },
});

export const themes = {
  pastelGreen: pastelGreenTheme,
  softBlue: softBlueTheme,
  warmPink: warmPinkTheme,
  midnight: midnightTheme,
};

export default themes;